/**
 * Operator archive route (OL-158) — eagerly registers
 * `/orca/task/$issueIdentifier/archive` and gates its param; the page
 * component, the archive atoms and their RPC imports are deferred to
 * `$issueIdentifier_.archive.lazy.tsx` so the panel's chunk stays out of the
 * always-loaded `main` bundle.
 *
 * The `_` on the param segment is an ESCAPE, not a typo: it opts this route out
 * of nesting under `task/$issueIdentifier.tsx`, whose page renders no
 * `<Outlet/>` and would therefore swallow this one. The escape also bypasses
 * that route's `orcaTaskDetailRouteBeforeLoad`, so the identifier check has to
 * be re-applied HERE — a junk segment must render the root not-found page
 * rather than reach an SSR read or an RPC.
 *
 * No `requireAuth` / `requireAdmin` call of its own: `/orca/task/...` is not an
 * `ORCA_PUBLIC_ROOTS` entry, so the `/orca` layout guard has already run
 * `requireAdmin` by the time this `beforeLoad` executes.
 *
 * @since 1.0.0
 */
import { createFileRoute, notFound } from "@tanstack/react-router";
import { isLinearIssueIdentifier } from "~/lib/orca-issue-identifier.ts";
import { orcaloopsStylesheetLinks } from "~/lib/orcaloops-head.ts";

/**
 * The guard, extracted so it is unit-testable without the route machinery.
 * Rejects any segment that is not a Linear issue identifier BEFORE any read.
 *
 * @since 1.0.0
 */
export const orcaTaskArchiveRouteBeforeLoad = (
  args: { readonly params?: Readonly<Record<string, string>> },
): Record<string, never> => {
  if (!isLinearIssueIdentifier(args.params?.issueIdentifier)) {
    throw notFound();
  }
  return {};
};

/**
 * The route's `head()`: the v5 orcaloops frame's stylesheet, linked here so it
 * is on the page before the lazy chunk's markup paints. Extracted so it is
 * unit-testable without the route machinery.
 *
 * @since 1.0.0
 */
export const taskArchiveHead = () => ({ links: orcaloopsStylesheetLinks });

export const Route = createFileRoute("/orca/task/$issueIdentifier_/archive")({
  beforeLoad: orcaTaskArchiveRouteBeforeLoad,
  head: taskArchiveHead,
});
